import { useEffect, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';

interface RichTextEditorProps {
  /** Markdown string. */
  value: string;
  /** Called with the edited Markdown. */
  onChange: (markdown: string) => void;
}

function ToolbarButton({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()} // keep the editor selection
      onClick={onClick}
      className={`px-2 py-1 text-xs font-medium border rounded ${
        active ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Visual (WYSIWYG) editor for event copy — authors see the formatting, not the
 * marks. Stores **Markdown** (via tiptap-markdown), so the members web render,
 * the WordPress plugin, and the Flutter app all keep a single portable format.
 */
export function RichTextEditor({ value, onChange }: RichTextEditorProps) {
  // Track the last Markdown we emitted so external value changes (edit /
  // duplicate / reset) re-seed the editor, but our own edits don't loop.
  const lastEmitted = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown.configure({ html: false, breaks: true, transformPastedText: true }),
    ],
    content: value,
    editorProps: {
      attributes: { class: 'min-h-[9rem] px-3 py-2 focus:outline-none' },
    },
    onUpdate: ({ editor }) => {
      const md = editor.storage.markdown.getMarkdown();
      lastEmitted.current = md;
      onChange(md);
    },
  });

  useEffect(() => {
    if (editor && value !== lastEmitted.current) {
      lastEmitted.current = value;
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) {
    return <div className="min-h-[9rem] rounded-lg border border-gray-300" />;
  }

  const b = (e: Editor) => e.chain().focus();

  return (
    <div className="rounded-lg border border-gray-300 overflow-hidden text-left">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1">
        <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => b(editor).toggleBold().run()}>
          <b>B</b>
        </ToolbarButton>
        <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => b(editor).toggleItalic().run()}>
          <i>I</i>
        </ToolbarButton>
        <ToolbarButton
          title="Heading"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => b(editor).toggleHeading({ level: 2 }).run()}
        >
          H
        </ToolbarButton>
        <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onClick={() => b(editor).toggleBulletList().run()}>
          • List
        </ToolbarButton>
        <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => b(editor).toggleOrderedList().run()}>
          1. List
        </ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        className="text-sm text-gray-800 [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-2 [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-2 [&_h3]:text-sm [&_h3]:font-bold [&_strong]:font-semibold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_p]:my-1"
      />
    </div>
  );
}
